import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

interface ImpersonationSession {
  token: string;
  expiresAt: number;
  targetUserId: string;
  targetUserName: string;
  targetUserEmail: string;
  readOnly: boolean;
}

interface ImpersonationContextType {
  session: ImpersonationSession | null;
  startImpersonation: (adminAccessToken: string, targetUserId: string, justification: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  getEffectiveToken: (originalToken: string) => string;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

export function ImpersonationProvider({ children, projectId }: { children: React.ReactNode, projectId: string }) {
  const [session, setSession] = useState<ImpersonationSession | null>(null);

  // Check for expired session periodically
  useEffect(() => {
    if (!session) return;
    const timer = setInterval(() => {
        if (Date.now() > session.expiresAt) {
            toast.error("Impersonation session expired");
            endImpersonation();
        }
    }, 30000);
    return () => clearInterval(timer);
  }, [session]);

  const startImpersonation = async (adminAccessToken: string, targetUserId: string, justification: string) => {
      try {
          const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/auth/impersonate/start`, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${adminAccessToken}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({ targetUserId, justification })
          });
          
          if (!response.ok) {
              const err = await response.json().catch(() => ({}));
              throw new Error(err.error || 'Failed to start impersonation');
          }
          
          const data = await response.json();
          setSession({
              token: data.impersonationToken,
              expiresAt: data.expiresAt,
              targetUserId: data.targetUserId,
              targetUserName: data.targetUserName,
              targetUserEmail: data.targetUserEmail,
              readOnly: data.readOnly
          });
          
          toast.success(`Viewing as ${data.targetUserName}`);
          
      } catch (error: any) {
          toast.error(error.message);
          throw error;
      }
  };

  const endImpersonation = async () => {
      if (!session) return;
      try {
          await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/auth/impersonate/end`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({ impersonationToken: session.token })
          });
          
      } catch (e) {
          console.error(e);
      } finally {
          setSession(null);
          toast.info("Exited impersonation");
      }
  };

  const getEffectiveToken = (originalToken: string) => {
      if (session && Date.now() < session.expiresAt) {
          return session.token;
      }
      return originalToken;
  };

  return (
    <ImpersonationContext.Provider value={{ session, startImpersonation, endImpersonation, isImpersonating: !!session, getEffectiveToken }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) throw new Error("useImpersonation must be used within ImpersonationProvider");
  return context;
};
